import { useEffect, useState, useCallback } from 'react'
import type { Page } from '../api/client'
import { invoicesApi, type Invoice } from '../api/finance-ext'
import {
  StatGrid, AccessDenied, Button, Card, CardContent, CircularProgress, Callout, DataTable, DataTableEmptyRow, Icon,
  IconButton, Page as PageLayout, PageHeader, Pagination, Select, StatCard, StatusPill, type PillTone,
  FilterBar, useConfirm,
} from '../components/ui'

const STATUS_COLORS: Record<string, PillTone> = {
  draft: 'neutral',
  sent: 'primary',
  paid: 'success',
  cancelled: 'error',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  sent: 'Inviata',
  paid: 'Pagata',
  cancelled: 'Annullata',
}

const PAGE_SIZE = 25

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Renders a redacted-amount cell: a lock icon with a tooltip when the backend
 * omitted the field (staff without CanSeeRealAmounts), otherwise the normal
 * formatted amount. */
function AmountCell({ value }: { value: number | undefined }) {
  if (value === undefined) {
    return (
      <span className="text-muted" title="Importo non visibile">
        <Icon name="lock" size={18} />
      </span>
    )
  }
  return <>{formatEur(value)}</>
}

export function ReportsPage() {
  const confirm = useConfirm()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Page<Invoice> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      const p: { page: number; status?: string } = { page }
      if (status) p.status = status
      const result = await invoicesApi.list(p)
      setData(result)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore'
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')) {
        setUnauthorized(true)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [status, page])

  useEffect(() => {
    void load()
  }, [load])

  async function handleUpdateStatus(id: number, newStatus: string): Promise<void> {
    try {
      await invoicesApi.updateStatus(id, newStatus)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore aggiornamento stato')
    }
  }

  async function handleDelete(id: number): Promise<void> {
    if (!(await confirm({ title: 'Eliminare la fattura?', message: 'Eliminare definitivamente questa fattura?', confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await invoicesApi.delete(id)
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione')
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const paidCount = items.filter(inv => inv.status === 'paid').length
  const totalAmount = items.reduce((sum, inv) => sum + (inv.total ?? 0), 0)
  const openCount = items.filter(inv => inv.status === 'draft' || inv.status === 'sent').length

  return (
    <PageLayout>
      <PageHeader
        title="Report fatture"
        icon="bar_chart"
      />

      {!unauthorized && (
        <FilterBar>
          <Select
            label="Stato"
            value={status}
            onChange={e => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Tutti</option>
            <option value="draft">Bozza</option>
            <option value="sent">Inviata</option>
            <option value="paid">Pagata</option>
            <option value="cancelled">Annullata</option>
          </Select>
        </FilterBar>
      )}

      {unauthorized && <AccessDenied requirement="il ruolo EqualStaff" />}

      {error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <>
          <StatGrid>
            <StatCard label="Totale fatture" value={total} />
            <StatCard label="Pagate" supporting="pagina corrente" value={paidCount} tone="success" />
            <StatCard label="Importo totale" supporting="pagina corrente" value={formatEur(totalAmount)} />
            <StatCard label="Scoperte" supporting="pagina corrente" value={openCount} tone="warning" />
          </StatGrid>

          <Card padding="none">
            {loading ? (
              <CardContent className="text-center">
                <CircularProgress size="sm" />
              </CardContent>
            ) : (
              <>
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Numero Fattura</th>
                      <th>Stato</th>
                      <th>Data Fattura</th>
                      <th>Scadenza</th>
                      <th className="md-num">Importo</th>
                      <th>Cliente</th>
                      <th>Contratto</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && <DataTableEmptyRow colSpan={8} icon="receipt_long" title="Nessuna fattura trovata" />}
                    {items.map(inv => (
                      <tr key={inv.id}>
                        <td className="md-typescale-emphasized-body-medium font-monospace">{inv.invoiceNumber}</td>
                        <td>
                          <StatusPill tone={STATUS_COLORS[inv.status] ?? 'neutral'}>
                            {STATUS_LABELS[inv.status] ?? inv.status}
                          </StatusPill>
                        </td>
                        <td>{formatDate(inv.invoiceDate)}</td>
                        <td>{inv.dueDate ? formatDate(inv.dueDate) : '—'}</td>
                        <td className="md-num"><AmountCell value={inv.total} /></td>
                        <td>{inv.customerId != null ? `#${inv.customerId}` : '—'}</td>
                        <td>{inv.contractId != null ? `#${inv.contractId}` : '—'}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            {inv.status === 'draft' && (
                              <Button size="sm" variant="outlined" onClick={() => void handleUpdateStatus(inv.id, 'sent')}>
                                Invia
                              </Button>
                            )}
                            {inv.status === 'sent' && (
                              <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => void handleUpdateStatus(inv.id, 'paid')}
                              >
                                Segna pagata
                              </Button>
                            )}
                            {(inv.status === 'draft' || inv.status === 'sent') && (
                              <Button size="sm" variant="text" onClick={() => void handleUpdateStatus(inv.id, 'cancelled')}>
                                Annulla
                              </Button>
                            )}
                            <IconButton
                              icon="delete"
                              size="sm"
                              aria-label="Elimina"
                              onClick={() => void handleDelete(inv.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
                {Math.ceil(total / PAGE_SIZE) > 1 && (
                  <div className="d-flex justify-content-center p-2">
                    <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
                  </div>
                )}
              </>
            )}
          </Card>
        </>
      )}
    </PageLayout>
  )
}
